/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commerical BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-09-04 04:10:20
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Field, getFormValues, change } from 'redux-form';
import { FormLayout, Popover } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import { RenderTextField } from 'src/components/views/common/forms/render-textfield';
import { RenderSelect } from 'src/components/views/common/forms/render-select';
import { RenderColorPicker2 } from 'src/components/views/common/forms/render-color-picker-2';

import {
  FILTER_SETTINGS_FONT_WEIGHT,
  FILTER_SETTINGS_FONT_TRANSFORM
} from 'src/constants/filter-settings';
import { hasTag } from 'src/helpers/form-validation-helper';
import {
  parseRGBAColor,
  stringifyRGBASpecialFormat,
  parseRGBAColorFromSpecialFormat
} from 'src/helpers/color-helper';

type Props = WithI18nProps;

class FitlerTitleTab extends Component<Props, State> {
  state = {
    rgbColor: '',
    rgbColorStr: '',
    activeColorPicker: false
  };

  componentDidMount() {
    this.displayColors(this.props);
  }

  componentWillReceiveProps(nextProps) {
    this.displayColors(nextProps);
  }

  displayColors = props => {
    const { filterTitleTextColor } = props;

    // Convert to rgba format
    if (_.isString(filterTitleTextColor)) {
      const rgbColorStr = filterTitleTextColor.replace(/\|\|/g, ', ');
      const rgbColor = parseRGBAColorFromSpecialFormat(filterTitleTextColor);
      this.setState({ rgbColor, rgbColorStr });
    }
  };

  render() {
    const { i18n } = this.props;

    const fontWeights = FILTER_SETTINGS_FONT_WEIGHT.map(function (option) {
      if (option.value === '')
        return {
          ...option,
          label: i18n.translate(option.label)
        };
      return option;
    });
    const fontTransforms = FILTER_SETTINGS_FONT_TRANSFORM.map(function (option) {
      return {
        ...option,
        label: i18n.translate(option.label)
      };
    });

    const { activeColorPicker, rgbColor, rgbColorStr } = this.state;

    const activator = (
      <div className="colorPickerBox">
        <div className="Polaris-Labelled__LabelWrapper">
          <div className="Polaris-Label">
            <label className="Polaris-Label__Text" style={{ display: 'inline-flex' }}>
              {i18n.translate('AdminHTML.FilterSettings.Common.textColor')}
            </label>
          </div>
        </div>
        <div className="Polaris-TextField">
          <div
            className="Polaris-ResourceList__Badge selectedColor"
            style={{ backgroundColor: rgbColorStr }}
            onClick={this.handleOpenColorPicker}></div>
          <input
            id="style.filterTitleTextColor_ColorPicker"
            type="text"
            className="Polaris-TextField__Input"
            value={rgbColorStr}
            onClick={this.handleOpenColorPicker}
            onChange={this.onInputColorValue}
          />
          <div className="Polaris-TextField__Backdrop"></div>
        </div>
      </div>
    );

    return (
      <FormLayout>
        {/* Open color picker */}
        <Popover
          active={activeColorPicker}
          activator={activator}
          onClose={this.handleOpenColorPicker}
          preferredAlignment="left">
          <Field
            name="style.filterTitleTextColor"
            component={RenderColorPicker2}
            color={rgbColor}
            onChange={this.handleChangeColor}
          />
        </Popover>

        <Field
          name="style.filterTitleFontSize"
          component={RenderTextField}
          label={i18n.translate('AdminHTML.FilterSettings.Common.fontSize')}
          validate={[hasTag]}
        />

        <Field
          name="style.filterTitleFontWeight"
          component={RenderSelect}
          label={i18n.translate('AdminHTML.FilterSettings.Common.fontWeight')}
          options={fontWeights}
        />

        <Field
          name="style.filterTitleFontTransform"
          component={RenderSelect}
          label={i18n.translate('AdminHTML.FilterSettings.Common.fontTransform')}
          options={fontTransforms}
        />
        <Field
          name="style.filterTitleFontFamily"
          component={RenderTextField}
          label={i18n.translate('AdminHTML.FilterSettings.Common.fontFamily')}
          validate={[hasTag]}
        />

        <div className="fontFamily-note">
          {i18n.translate(
            'AdminHTML.FilterSettings.Common.fontNotAvailableForPreviewDisplayingDefaultFontInsteadForPreviewinPurposeOnly'
          )}
        </div>
      </FormLayout>
    );
  }

  handleOpenColorPicker = () => {
    this.setState(({ activeColorPicker }) => ({
      activeColorPicker: !activeColorPicker
    }));
  };

  handleChangeColor = color => {
    const { rgb: rgbColor } = color;
    this.setState({ rgbColor });
  };

  onInputColorValue = e => {
    const { updateField } = this.props;
    const { rgbColor: rgbColorPrev = {} } = this.state;

    // Parse string to object { r, g, b, a }
    let input = e.target.value;
    const color = parseRGBAColor(input);
    const rgbColor = !_.isEmpty(color) ? color : rgbColorPrev;

    this.setState({ rgbColor, rgbColorStr: input });

    // update color on redux-form
    updateField('style.filterTitleTextColor', { rgb: rgbColor });
  };
}

const mapStateToProps = (state, props) => {
  const formFilterSettingsValues = getFormValues('FilterSettingsForm')(state);

  let filterTitleTextColor = _.get(formFilterSettingsValues, 'style.filterTitleTextColor', {
    rgb: {
      r: 255,
      g: 255,
      b: 255,
      a: 1
    }
  });

  if (_.has(filterTitleTextColor, 'rgb')) {
    filterTitleTextColor = stringifyRGBASpecialFormat(filterTitleTextColor.rgb);
  }

  return {
    filterTitleTextColor
  };
};

const mapDispatchToProps = dispatch => ({
  updateField: (field, data) => dispatch(change('FilterSettingsForm', field, data))
});

export default connect(mapStateToProps, mapDispatchToProps)(withI18n()(FitlerTitleTab));
